import { useState, useCallback, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InputGroupItem } from '@/components/ui/input-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SelectedDomain, EmailAccount } from './types';

const MAX_ACCOUNTS_PER_DOMAIN = 3;

interface AddSenderFormProps {
  domains: SelectedDomain[];
  accounts: EmailAccount[];
  onAddSender: (username: string, domain: string, firstName: string, lastName: string) => void;
}

function InlineInput(props: React.ComponentProps<typeof InputGroupItem>) {
  return (
    <div className="rounded-3xl bg-input-bg border border-control-border overflow-hidden shadow-lg">
      <InputGroupItem {...props} />
    </div>
  );
}

export function AddSenderForm({ domains, accounts, onAddSender }: AddSenderFormProps) {
  const [username, setUsername] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string>(domains[0]?.domain || '');
  const [senderName, setSenderName] = useState('');

  // Sync selectedDomain when domains change
  useEffect(() => {
    if (domains.length > 0 && !domains.some(d => d.domain === selectedDomain)) {
      setSelectedDomain(domains[0].domain);
    }
  }, [domains, selectedDomain]);
  const [lastName, setLastName] = useState('');
  const [showLimitWarning, setShowLimitWarning] = useState(false);

  // Find domains that have reached the 5 account limit
  const domainsAtLimit = domains
    .filter(d => accounts.filter(a => a.domain === d.domain).length >= MAX_ACCOUNTS_PER_DOMAIN)
    .map(d => d.domain);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    setUsername(value);
    setShowLimitWarning(false);
  };

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    // Show warning if domains are at limit, but still allow submission
    if (domainsAtLimit.length > 0) {
      setShowLimitWarning(true);
    }

    onAddSender(username.trim(), selectedDomain, senderName.trim(), lastName.trim());
    setUsername('');
    setSenderName('');
    setLastName('');
  }, [username, selectedDomain, senderName, lastName, onAddSender, domainsAtLimit.length]);

  return (
    <div className="space-y-3">
      <p className="text-base font-medium pl-4 mt-2 mb-4">Add Custom Email Address</p>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Email input: username @ domain dropdown */}
        <div className="inline-flex items-center rounded-3xl border border-control-border shadow-lg overflow-hidden">
          <div className="bg-input-bg w-56">
            <InputGroupItem
              id="dfy-username"
              value={username}
              onChange={handleUsernameChange}
              placeholder="e.g., sales"
              required
              pattern="[a-z0-9._-]+"
              title="Only lowercase letters, numbers, dots, underscores, and hyphens"
            />
          </div>

          <div className="flex items-center h-12 pl-3 pr-2 bg-input-bg text-sm text-muted-foreground">
            @
          </div>

          <div className="relative z-10 flex items-center h-12 bg-input-bg">
            <div className="h-6 w-px bg-divider" />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="relative z-10 h-12 px-4 bg-input-bg flex items-center gap-2 text-sm hover:bg-input-bg transition-colors cursor-pointer"
              >
                <span className="truncate max-w-[150px]">{selectedDomain === 'all' ? 'All domains' : selectedDomain || 'Select domain'}</span>
                <ChevronDown className="h-4 w-4 flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {domains.length > 1 && (
                <DropdownMenuItem onClick={() => setSelectedDomain('all')}>
                  All domains
                </DropdownMenuItem>
              )}
              {domains.map((domain) => (
                <DropdownMenuItem
                  key={domain.domain}
                  onClick={() => setSelectedDomain(domain.domain)}
                >
                  {domain.domain}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Sender Name and Last Name inputs */}
        <div className="flex gap-3">
          <InlineInput
            id="dfy-senderName"
            value={senderName}
            onChange={(e) => {
              setSenderName(e.target.value);
              setShowLimitWarning(false);
            }}
            placeholder="e.g., Sarah"
            inlineLabel="Sender Name"
            labelWidth="100px"
            required
          />
          <InlineInput
            id="dfy-lastName"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setShowLimitWarning(false);
            }}
            placeholder="e.g., Jones"
            inlineLabel="Last Name"
            labelWidth="90px"
            required
          />
        </div>

        {/* Warning message and submit button */}
        <div className="pt-2 flex items-center justify-between">
          <div>
            {showLimitWarning && domainsAtLimit.length > 0 && (
              <p className="text-sm text-warning-foreground pl-4">
                We recommend a maximum of 3 emails per domain for the best deliverability.
                You have 3 or more emails with the {domainsAtLimit.join(', ')} domain{domainsAtLimit.length > 1 ? 's' : ''}.
              </p>
            )}
          </div>
          <Button type="submit" variant="primary" className="-mr-2 -mb-2">
            Add Email Address
          </Button>
        </div>
      </form>
    </div>
  );
}
